// @flow
import {ofType, StateObservable} from 'redux-observable';
import {
  catchError,
  concatMap,
  debounceTime,
  map,
  mergeMap,
  switchMap,
  delay,
} from 'rxjs/operators';
import {ActionCreator, buildCatchError, createReducer} from '../../store/utils';

import {
  endpoints,
  getCollaboratorInfoApi,
  // createOrderApi,
  // deleteOrderApi,
  // editOrderApi,
  listOrdersApi,
  listPreOrdersApi,
  ordersCountApi,
} from './api';
import {buildRoute} from '../../http';
import {of} from 'rxjs';
import {
  getOrderTaxInvoiceInformationListByIdsApi,
  getOrderTaxInvoiceListByIdsApi,
} from '../tax-invoice/api';

import type {ActionsObservable} from 'redux-observable';
import type {Action} from '../../store/utils';
import type {
  Order,
  OrderState,
  Pagination,
  OrderTaxInvoiceData,
} from '../../entities';
import type {ApiError} from '../../http/error';
import {
  UpdateOrderCollaboratorSuccess,
  UpdateOrderStateSuccess,
  UpdatePreOrderStateSuccess,
} from '../order/duck';
import type {RootState} from '..';

export type OrdersAction = Action & {
  orders?: Order[],
  order?: Order,
  orderId?: string,
  pagination?: Pagination,
  queryFilter?: {[key: string]: string},
  error?: string | ApiError,
  taxInvoiceData?: OrderTaxInvoiceData[],
};

export type MiniCollaborator = {};

export type OrdersState = {
  isFetching?: boolean,
  orders?: Order[],
  pagination?: Pagination,
  ordersCount?: number,
  fetchOrdersError?: string,
  selectedStates: OrderState[],
  queryFilter?: {[key: string]: string},
  tempCollaborators?: {[key: string]: any},
};

// Fetch order actions
export class FetchOrders extends ActionCreator<OrdersAction> {}
export class FetchOrdersSuccess extends ActionCreator<OrdersAction> {}
export class FetchOrdersError extends ActionCreator<OrdersAction> {}

export class FetchPreOrders extends ActionCreator<OrdersAction> {}
export class FetchPreOrdersSuccess extends ActionCreator<OrdersAction> {}
export class FetchPreOrdersError extends ActionCreator<OrdersAction> {}

export class FetchOrdersCount extends ActionCreator<OrdersAction> {}
export class FetchOrdersCountSuccess extends ActionCreator<OrdersAction> {}
export class FetchOrdersCountError extends ActionCreator<OrdersAction> {}

export class UpdateSelectedStates extends ActionCreator<OrdersAction> {}
export class ToggleSelectedState extends ActionCreator<OrdersAction> {}

export class ResetFilters extends ActionCreator<OrdersAction> {}
export class ResetPartialFilters extends ActionCreator<OrdersAction> {}

export class FetchCollaboratorInfo extends ActionCreator<OrdersAction> {}
export class FetchCollaboratorInfoSuccess extends ActionCreator<OrdersAction> {}
export class FetchCollaboratorInfoError extends ActionCreator<OrdersAction> {}

export class UpdateOrder extends ActionCreator<OrdersAction> {}

export class UpdateOrderTaxInvoice extends ActionCreator<OrdersAction> {}

export class FetchOrdersTaxInvoiceData extends ActionCreator<OrdersAction> {}
export class FetchOrdersTaxInvoiceDataSuccess extends ActionCreator<OrdersAction> {}
export class FetchOrdersTaxInvoiceDataError extends ActionCreator<OrdersAction> {}

export const actions = {
  fetchOrders: (payload: OrdersAction) => new FetchOrders(payload),
  fetchCollaboratorInfo: (payload: OrdersAction) =>
    new FetchCollaboratorInfo(payload),
  fetchPreOrders: (payload: OrdersAction) => new FetchPreOrders(payload),
  updateSelectedStates: (orderStates: OrderState[]) =>
    new UpdateSelectedStates({
      payload: orderStates,
    }),
  toggleSelectedState: (orderState: OrderState) =>
    new ToggleSelectedState({
      payload: orderState,
    }),
  fetchOrdersCount: (payload?: OrdersAction) => new FetchOrdersCount(payload),
  resetFilters: () => new ResetFilters(),
  resetPartialFilters: () => new ResetPartialFilters(),

  updateOrder: (order: Order) => new UpdateOrder({order}),
  updateOrderTaxInvoice: (
    orderId: string,
    taxInvoiceData: OrderTaxInvoiceData[]
  ) => new UpdateOrderTaxInvoice({taxInvoiceData, orderId}),
};

export const initialState: OrdersState = {
  selectedStates: ['PENDING'],
  tempCollaborators: {},
};

export default createReducer(initialState, {
  // Fetching orders
  [FetchOrders.type]: (state: OrdersState, action: OrdersAction) => {
    return {
      ...state,
      orders: undefined,
      pagination: undefined,
      isFetching: true,
      fetchOrdersError: undefined,
      modalOpen: false,
      queryFilter: {
        ...state.queryFilter,
        ...action.queryFilter,
      },
    };
  },
  [FetchOrdersSuccess.type]: (state: OrdersState, action: OrdersAction) => {
    const states = state.selectedStates;

    const shouldHideCancelledOrders =
      states.length === 0 || states.includes('ALL');

    return {
      ...state,
      isFetching: false,
      orders: action.orders.filter(order => {
        if (!shouldHideCancelledOrders) return true;
        return order.orderState !== 'CANCELLED';
      }),
      pagination: action.pagination,
      fetchOrdersError: undefined,
    };
  },
  [FetchOrdersError.type]: (state: OrdersState, action: OrdersAction) => ({
    ...state,
    isFetching: false,
    fetchOrdersError: action.error,
  }),
  [FetchPreOrders.type]: (state: OrdersState, action: OrdersAction) => ({
    ...state,
    orders: undefined,
    pagination: undefined,
    isFetching: true,
    fetchOrdersError: undefined,
    modalOpen: false,
    queryFilter: action.queryFilter,
  }),
  [FetchPreOrdersSuccess.type]: (state: OrdersState, action: OrdersAction) => ({
    ...state,
    isFetching: false,
    orders: action.orders,
    pagination: action.pagination,
    fetchOrdersError: undefined,
  }),
  [FetchPreOrdersError.type]: (state: OrdersState, action: OrdersAction) => ({
    ...state,
    isFetching: false,
    fetchOrdersError: action.error,
  }),
  [UpdateSelectedStates.type]: (
    state: OrdersState,
    {payload}: OrdersAction
  ) => ({...state, selectedStates: payload}),
  [ToggleSelectedState.type]: (state: OrdersState, {payload}: OrdersAction) => {
    if (payload === 'ALL') {
      return {
        ...state,
        selectedStates: [],
      };
    } else if (payload === 'PRE_ORDERS') {
      return state.selectedStates[0] === 'PRE_ORDERS'
        ? {
            ...state,
            selectedStates: [],
          }
        : {
            ...state,
            selectedStates: ['PRE_ORDERS'],
          };
    } else {
      const isActive = state.selectedStates.includes(payload);

      return {
        ...state,
        selectedStates: isActive ? [] : [payload],
      };
      // if (state.selectedStates[0] === 'PRE_ORDERS') {
      //   return {
      //     ...state,
      //     selectedStates: [payload],
      //   };
      // } else
      //   return state.selectedStates.indexOf(payload) === -1
      //     ? {
      //         ...state,
      //         selectedStates: [...state.selectedStates, payload],
      //       }
      //     : {
      //         ...state,
      //         selectedStates: state.selectedStates.filter(e => e !== payload),
      //       };
    }
  },
  [FetchOrdersCount.type]: (state: OrdersState, action: OrdersAction) => ({
    ...state,
    isFetching: true,
    fetchOrdersError: undefined,
  }),
  [FetchOrdersCountSuccess.type]: (
    state: OrdersState,
    action: OrdersAction
  ) => ({
    ...state,
    isFetching: undefined,
    ordersCount: action.count,
  }),
  [FetchOrdersCountError.type]: (state: OrdersState, action: OrdersAction) => ({
    ...state,
    isFetching: undefined,
    fetchOrdersError: action.error,
  }),
  [ResetFilters.type]: (state: OrderState) => ({
    ...state,
    selectedStates: ['PENDING'],
  }),
  [ResetPartialFilters.type]: (state: OrdersState) => ({
    ...state,
    selectedStates:
      state.queryFilter && state.queryFilter.states
        ? state.queryFilter.states.split(',')
        : ['PENDING'],
  }),
  [UpdateOrderStateSuccess.type]: (
    state: OrdersState,
    {order}: OrdersAction
  ) => {
    const aux = [...(state.orders || [])];
    const index = aux.findIndex(c => c.id === order.id);
    if (index > -1) {
      if (order.newState !== 'CANCELLED') {
        aux[index] = {
          ...aux[index],
          ...order,
        };
      } else {
        aux.splice(index, 1);
      }
    }

    return {
      ...state,
      orders: aux,
    };
  },
  [UpdatePreOrderStateSuccess.type]: (
    state: OrdersState,
    {order}: OrdersAction
  ) => {
    const aux = [...(state.orders || [])];
    const index = aux.findIndex(c => c.id === order.id);
    if (index > -1) {
      if (order.newState !== 'CANCELLED') {
        aux[index] = order;
      } else {
        aux.splice(index, 1);
      }
    }
    return {
      ...state,
      orders: aux,
    };
  },
  [FetchCollaboratorInfo.type]: (
    state: OrdersState,
    {collaboratorInfoRequest}: OrdersAction
  ) => {
    let tempCollaborators = state.tempCollaborators || {};
    tempCollaborators[collaboratorInfoRequest.collaboratorId] = {};
    return {
      ...state,
      tempCollaborators,
    };
  },
  [FetchCollaboratorInfoSuccess.type]: (
    state: OrdersState,
    action: OrdersAction
  ) => {
    let tempCollaborators = state.tempCollaborators;
    if (action.collaboratorId && tempCollaborators) {
      tempCollaborators = {
        ...tempCollaborators,
        [action.collaboratorId]: {data: action.data},
      };
    }
    return {
      ...state,
      tempCollaborators,
    };
  },
  [FetchCollaboratorInfoError.type]: (
    state: OrdersState,
    action: OrdersAction
  ) => {
    return {
      ...state,
      error: action.error,
    };
  },
  [UpdateOrder.type]: (
    state: OrdersState,
    {order}: OrdersAction
  ): OrdersState => {
    const orders = state.orders || [];

    const updatedOrders = orders.map(o => {
      if (o.id !== order.id) return o;
      return {
        ...o,
        ...order,
      };
    });

    return {
      ...state,
      orders: updatedOrders,
    };
  },
  [UpdateOrderTaxInvoice.type]: (
    state: OrdersState,
    {taxInvoiceData, orderId}: OrdersAction
  ): OrdersState => {
    const orders = state.orders || [];

    const updatedOrders = orders.map(order => {
      if (order.id !== orderId) return order;
      const prevTaxInvoiceList = order.taxInvoiceData?.taxInvoiceList || [];
      return {
        ...order,
        taxInvoiceData: {
          ...order.taxInvoiceData,
          taxInvoiceList: [...taxInvoiceData, ...prevTaxInvoiceList],
        },
      };
    });

    return {
      ...state,
      orders: updatedOrders,
    };
  },

  [FetchOrdersTaxInvoiceData.type]: (
    state: OrdersState,
    {orderIds}: OrdersAction
  ): OrdersState => {
    return {
      ...state,
      isFetching: true,
    };
  },
  [FetchOrdersTaxInvoiceDataSuccess.type]: (
    state: OrdersState,
    {taxInvoiceData}: OrdersAction
  ): OrdersState => {
    if (!taxInvoiceData || taxInvoiceData.length === 0) return state;

    const orders = state.orders || [];

    const updatedOrders = orders.map((order, index) => {
      const taxInvoice = taxInvoiceData[index];
      if (!taxInvoice) return order;
      if (
        taxInvoice.orderTaxInvoices &&
        taxInvoice.orderTaxInvoices.length === 0
      ) {
        return order;
      }
      console.log({taxInvoice});

      return {
        ...order,
        taxInvoiceData: {
          ...order.taxInvoiceData,
          taxInvoiceList: taxInvoice.orderTaxInvoices,
        },
      };
    });

    return {
      ...state,
      isFetching: false,
      taxInvoiceData,
      orders: updatedOrders,
    };
  },
});

export function fetchOrdersEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(FetchOrders.type, UpdateOrderCollaboratorSuccess.type),
    debounceTime(250),
    switchMap(({queryFilter}: OrdersAction) => {
      const defaultPageSize = state$.value.orders.pagination?.size || 10;
      const query = {
        ...state$.value.orders.queryFilter,
        size: queryFilter?.size || defaultPageSize,
        states: state$.value.orders.selectedStates.join(','),
      };

      return listOrdersApi(query).pipe(
        map(
          ({data, pagination}: {data: Order[], pagination: Pagination}) => [
            data,
            pagination,
            null,
          ]
          // new FetchOrdersSuccess({orders: data, pagination})
        ),
        catchError(() => of([null, null, 'Error al obtener pedidos']))
        // buildCatchError(FetchOrdersError, 'Error al obtener pedidos')
      );
    }),
    concatMap((param: [Order[], Pagination, string]) => {
      const [orders, pagination, error] = param;
      if (error) return of([orders, pagination, error]);

      if (orders.length === 0) {
        return of([orders, pagination, null]);
      }

      return getOrderTaxInvoiceInformationListByIdsApi({
        order_ids: orders.map(o => o.id).join(','),
      }).pipe(
        map(data => {
          const orderWithTaxInvoice = orders.map((o, index) => {
            const taxInvoice = data[index];

            if (!taxInvoice) return o;

            const firstTaxInvoice = taxInvoice.orderTaxInvoices[0];

            if (!firstTaxInvoice) return o;

            return {
              ...o,
              taxInvoiceData: {
                taxInvoiceInformation: {
                  taxReceiverCode: firstTaxInvoice.taxReceiverCode,
                  taxReceiverName: firstTaxInvoice.taxReceiverName,
                  address: firstTaxInvoice.address,
                  admin1: firstTaxInvoice.admin1,
                  admin2: firstTaxInvoice.admin2,
                  admin3: firstTaxInvoice.admin3,
                  id: firstTaxInvoice.id,
                  taxInvoiceType: firstTaxInvoice.taxInvoiceType,
                },
              },
            };
          });
          return [orderWithTaxInvoice, pagination, null];
        }),
        catchError(() =>
          of([orders, pagination, 'Error al obtener información de facturas'])
        )
      );
    }),
    concatMap(([orders, pagination, error]: [Order[], Pagination, string]) => {
      if (error) return of(new FetchOrdersSuccess({orders, pagination}));
      if (orders.length === 0) {
        return of(
          new FetchOrdersSuccess({orders: orders, pagination: pagination})
        );
      }
      return getOrderTaxInvoiceListByIdsApi({
        order_ids: orders.map(o => o.id).join(','),
      }).pipe(
        map(data => {
          const orderWithTaxInvoice = orders.map((o, index) => {
            const taxInvoice = data[index];

            if (!taxInvoice) return o;

            const firstTaxInvoice = taxInvoice.orderTaxInvoices[0];

            if (!firstTaxInvoice) return o;

            return {
              ...o,
              taxInvoiceData: {
                ...o.taxInvoiceData,
                taxInvoiceList: taxInvoice.orderTaxInvoices,
              },
            };
          });

          return new FetchOrdersSuccess({
            orders: orderWithTaxInvoice,
            pagination,
          });
        }),
        catchError(() => of(new FetchOrdersSuccess({orders, pagination})))
      );
    })
  );
}

export function fetchPreOrdersEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(FetchPreOrders.type),
    debounceTime(250),
    switchMap(({queryFilter}: OrdersAction) => {
      let query = undefined;
      if (queryFilter) query = queryFilter;
      else if (state$.value.orders.queryFilter)
        query = {...state$.value.orders.queryFilter};
      return listPreOrdersApi(query).pipe(
        map(
          ({data, pagination}: {data: Order[], pagination: Pagination}) =>
            new FetchPreOrdersSuccess({orders: data, pagination})
        ),
        buildCatchError(FetchPreOrdersError, 'Error al obtener pedidos')
      );
    })
  );
}

export function fetchOrdersCountEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(FetchOrdersCount.type),
    switchMap(({noFilters, dateRange, orderStates = []}) => {
      let qf = {};
      qf.states = orderStates.join(',');
      if (dateRange) {
        qf = {...qf, ...dateRange, init_date: dateRange.start_date};
        delete qf.start_date;
      }
      return ordersCountApi(qf).pipe(
        map(data => new FetchOrdersCountSuccess({count: data.count})),
        buildCatchError(
          FetchOrdersCountError,
          'Error al obtener numero de pedidos'
        )
      );
    })
  );
}

export function fetchCollaboratorInfoEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(FetchCollaboratorInfo.type),
    mergeMap(({collaboratorInfoRequest}: OrdersAction) => {
      const collaboratorId: string = collaboratorInfoRequest.collaboratorId;
      return getCollaboratorInfoApi(undefined, {
        route: buildRoute(endpoints.GET_COLLABORATOR_INFO, [collaboratorId]),
      }).pipe(
        map(data => new FetchCollaboratorInfoSuccess({collaboratorId, data})),
        buildCatchError(FetchCollaboratorInfoError)
      );
    })
  );
}

export function fetchOrdersTaxInvoiceDataEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(FetchOrdersTaxInvoiceData.type, UpdateOrderStateSuccess.type),
    debounceTime(250),
    delay(500),
    mergeMap(() => {
      const isInOrderDetailPage = !!state$.value.order.order;

      if (isInOrderDetailPage) {
        return of(new FetchOrdersTaxInvoiceDataSuccess({taxInvoiceData: []}));
      }

      const orderIds = state$.value.orders.orders?.map(o => o.id) || [];

      if (orderIds.length === 0) {
        return of(new FetchOrdersTaxInvoiceDataSuccess({taxInvoiceData: []}));
      }

      const orderIdsString = orderIds.join(',');

      return getOrderTaxInvoiceListByIdsApi({order_ids: orderIdsString}).pipe(
        map(
          data => new FetchOrdersTaxInvoiceDataSuccess({taxInvoiceData: data})
        ),
        buildCatchError(FetchOrdersTaxInvoiceDataError)
      );
    })
  );
}
